import React, { createContext, useContext, useState, useCallback } from 'react';

const AIChatContext = createContext(null);

export const AIChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([]); // { role: 'user' | 'assistant', content: string }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendMessage = useCallback(async (text) => {
    if (!text?.trim()) return;

    const newUserMessage = { role: 'user', content: text.trim() };
    const nextMessages = [...messages, newUserMessage];
    setMessages(nextMessages);
    setIsLoading(true);
    setError(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        throw new Error('Backend URL not configured');
      }

      const res = await fetch(`${backendUrl}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nextMessages }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Request failed with status ${res.status}`);
      }

      const data = await res.json();
      const assistantMessage = { role: 'assistant', content: data.reply };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error('AI assistant error', err);
      setError(err.message || 'AI assistant error');
    } finally {
      setIsLoading(false);
    }
  }, [messages]);

  const resetChat = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const addLocalTurn = useCallback((role, content) => {
    if (!content) return;
    setMessages((prev) => [...prev, { role, content }]);
  }, []);

  const value = {
    messages,
    isLoading,
    error,
    sendMessage,
    resetChat,
    addLocalTurn,
  };

  return <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>;
};

export const useAIChat = () => {
  const ctx = useContext(AIChatContext);
  if (!ctx) throw new Error('useAIChat must be used within AIChatProvider');
  return ctx;
};
